import { ARCADE_RELEASE_OVERRIDES, type ArcadeReleaseOverride } from "../../content/arcadeReleases";
import { ARCADE_GAME_KINDS, isArcadeGameKind, type ArcadeGameKind } from "./games";

export const ARCADE_PATTERNS = ["grid", "rings", "stars", "waves"] as const;
export type ArcadePattern = (typeof ARCADE_PATTERNS)[number];

export const PALETTE_CHOICES = ["cover", "neon", "gold"] as const;
export type PaletteChoice = (typeof PALETTE_CHOICES)[number];

export const PALETTE_LABELS: Record<PaletteChoice, string> = { cover: "Cover", neon: "Neon", gold: "Gold" };

export const isPaletteChoice = (value: unknown): value is PaletteChoice =>
  typeof value === "string" && (PALETTE_CHOICES as readonly string[]).includes(value);

export interface ArcadePalette {
  hue: number;
  primary: string;
  secondary: string;
  accent: string;
  danger: string;
  background: string;
  backgroundDeep: string;
  text: string;
  lanes: string[];
}

export interface ArcadeTheme {
  seed: number;
  palette: ArcadePalette;
  pattern: ArcadePattern;
  bpm: number;
  featuredGame: ArcadeGameKind;
  games: ArcadeGameKind[];
}

export interface ThemeSongInput {
  slug: string;
  release?: { id?: string };
}

// FNV-1a, so a song always gets the same look and the same featured game.
export function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

// mulberry32: small, fast and seedable, so games replay the same way for a seed.
export function createRng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const wrapHue = (hue: number) => ((hue % 360) + 360) % 360;
const hsl = (h: number, s: number, l: number, a = 1) =>
  a >= 1 ? `hsl(${Math.round(wrapHue(h))} ${Math.round(s)}% ${Math.round(l)}%)` : `hsl(${Math.round(wrapHue(h))} ${Math.round(s)}% ${Math.round(l)}% / ${a})`;

// Reads the hue and saturation out of an "rgb(r g b)" or "rgb(r, g, b)" string,
// which is the shape useCoverTint hands back.
export function hueFromRgb(value: string | null | undefined): { hue: number; saturation: number } | null {
  const match = String(value || "").match(/rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  if (!match) return null;
  const [r, g, b] = [Number(match[1]) / 255, Number(match[2]) / 255, Number(match[3]) / 255];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
  let hue = 0;
  if (delta !== 0) {
    if (max === r) hue = 60 * (((g - b) / delta) % 6);
    else if (max === g) hue = 60 * ((b - r) / delta + 2);
    else hue = 60 * ((r - g) / delta + 4);
  }
  return { hue: wrapHue(hue), saturation };
}

const GOLD_HUE = 38;

// Hazards are red unless the song itself is red, where they would disappear
// into the backdrop, so those songs get violet hazards instead.
const dangerFor = (hue: number) => {
  const distance = Math.abs(((wrapHue(hue) - 352 + 540) % 360) - 180);
  return distance < 34 ? hsl(282, 80, 68) : hsl(352, 85, 60);
};

export function paletteFor(hue: number, choice: PaletteChoice): ArcadePalette {
  if (choice === "gold") {
    return {
      hue: GOLD_HUE,
      primary: hsl(GOLD_HUE, 62, 62),
      secondary: hsl(GOLD_HUE - 12, 48, 48),
      accent: hsl(GOLD_HUE + 8, 80, 82),
      danger: hsl(8, 70, 58),
      background: hsl(GOLD_HUE, 22, 9),
      backgroundDeep: hsl(GOLD_HUE, 24, 4),
      text: hsl(GOLD_HUE, 40, 92),
      lanes: [hsl(GOLD_HUE - 10, 60, 60), hsl(GOLD_HUE, 70, 66), hsl(GOLD_HUE + 12, 60, 70), hsl(GOLD_HUE + 24, 45, 76)],
    };
  }
  const base = choice === "neon" ? hue + 150 : hue;
  const saturation = choice === "neon" ? 96 : 78;
  return {
    hue: wrapHue(base),
    primary: hsl(base, saturation, 62),
    secondary: hsl(base + 38, saturation - 8, 56),
    accent: hsl(base + 180, 88, 66),
    danger: dangerFor(base),
    background: hsl(base, 42, choice === "neon" ? 7 : 10),
    backgroundDeep: hsl(base + 12, 48, 4),
    text: hsl(base, 30, 95),
    lanes: [hsl(base, saturation, 62), hsl(base + 50, saturation, 62), hsl(base + 180, saturation, 64), hsl(base + 230, saturation, 66)],
  };
}

export interface ArcadeThemeOptions {
  coverTint?: string | null;
  choice?: PaletteChoice;
  override?: ArcadeReleaseOverride;
}

export function arcadeThemeFor(song: ThemeSongInput, options: ArcadeThemeOptions = {}): ArcadeTheme {
  const override = options.override ?? ARCADE_RELEASE_OVERRIDES[song.slug] ?? {};
  const seed = hashString(`${song.slug}:${song.release?.id ?? ""}`);
  const rng = createRng(seed);

  const tint = hueFromRgb(options.coverTint);
  const seededHue = Math.floor(rng() * 360);
  const hue = typeof override.hue === "number" ? wrapHue(override.hue) : tint && tint.saturation >= 0.12 ? tint.hue : tint ? GOLD_HUE : seededHue;

  const pattern = override.pattern ?? ARCADE_PATTERNS[Math.floor(rng() * ARCADE_PATTERNS.length)];
  const bpm = typeof override.bpm === "number" && override.bpm >= 50 && override.bpm <= 200 ? override.bpm : 84 + Math.floor(rng() * 45);

  const games = (override.games ?? []).filter(isArcadeGameKind);
  const offered = games.length > 0 ? games : [...ARCADE_GAME_KINDS];
  const featuredGame = override.featuredGame && offered.includes(override.featuredGame)
    ? override.featuredGame
    : offered[seed % offered.length];

  return {
    seed,
    palette: paletteFor(hue, options.choice ?? "cover"),
    pattern,
    bpm,
    featuredGame,
    games: offered,
  };
}
